// General import
import React, { useState, useEffect, useRef } from 'react'

import * as d3 from 'd3'
import { useBeforeunload } from 'react-beforeunload'
import LZString from 'lz-string'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'

// Import from local files
import { SuiteSankeyNode, SuiteSankeyLink, SuiteSankeyData } from './types'
import Login from './components/Login/Login'
import Dashboard from './components/UserPages/Dashboard'
import Account from './components/UserPages/Account'
import LicenseCheckIn from './components/LicenseCheckIn/LicenseCheckIn'
import SankeySuiteMenu from './SankeySuiteMenu'

// Import From AFM
import { linkTooltipsContent, nodeTooltipsContent } from 'afmsankey/dist/SankeyTooltip'
import { afm_default_node, afm_default_link, uncert_link_text, link_text, node_arrow_visible } from 'afmsankey/dist/AFMSankeyMenu'


// Import from opensankey
import SankeyDraw from 'open-sankey/dist/SankeyDraw'

import * as OpenSankeyUtils from 'open-sankey/dist/SankeyUtils'
import * as OpenSankeyTooltip from 'open-sankey/dist/SankeyTooltip'
import { OpenSankeyDrawNodes } from 'open-sankey/dist/SankeyDrawNodes'
import { OpenSankeyDrawLinks } from 'open-sankey/dist/SankeyDrawLinks'
import { OpenSankeyDrawLegend } from 'open-sankey/dist/SankeyDrawLegend'
import { OpenSankeyDrawNodesLabel } from 'open-sankey/dist/SankeyDrawNodesLabel'
import * as OpenSankeyDrawFunction from 'open-sankey/dist/SankeyDrawFunction'

// Import Form SankeyPlus
import { SankeyPlusDrawNodesIcon,SankeyPlusNodeClickEvent } from 'sankeyanimation/dist/SankeyPlusNodes'
import { SankeyPlusDrawLabels,sankey_plus_min_width_and_height } from 'sankeyanimation/dist/SankeyPlusLabels'
import * as SankeyPlusGradient from 'sankeyanimation/dist/SankeyPlusGradient'
import { SankeyPlusDrawNodesFO} from 'sankeyanimation/dist/SankeyPlusForeignObject'
import * as SankeyViews from 'sankeyanimation/dist/SankeyPlusViews'

// Import From sankeyDev
import { hiddeLoneNodes, searchAndRestoreLoneNodes } from 'sankeydev/dist/HideLoneNodeFunction'
import { devGetLinkValue } from 'sankeydev/dist/PercentValue'

declare const window: Window &
typeof globalThis & {
  SankeyToolsStatic: boolean
  sankey: {
    sankey_data_file:RequestInfo
    sous_filieres : { [ key : string ] : string }
    units: string[]
    flask_logo? : string
    flask_header? : string
    logo_width? : number
    legend_average : string
    legend_uncert : string
    help_text : string
    welcome_text: string
    excel : string
    logo: string,
    advanced: boolean
  }
}


type SankeyAppTypes = {
  initial_sankey_data : SuiteSankeyData
  exemple_menu        : object
  formations_menu      : object
  logo: string,
  logo_terriflux: string,
  token: boolean,
  set_token:(b:boolean)=>void,
  has_sankey_suite: boolean,
  set_sankey_suite:(b:boolean)=>void,
  has_open_sankey_plus: boolean,
  set_open_sankey_plus:(b:boolean)=>void,
  has_sankeydev:boolean,
  set_sankeydev:(b:boolean)=>void,
  unsetTokens: ()=>void,
  useNavigate:()=>any,
  get_default_data:()=>SuiteSankeyData
}


export const SankeyApp = ({
  initial_sankey_data,
  exemple_menu,
  formations_menu,
  logo,
  logo_terriflux,
  token,
  set_token,
  has_sankey_suite,
  set_sankey_suite,
  has_open_sankey_plus,
  set_open_sankey_plus,
  has_sankeydev,
  set_sankeydev,
  unsetTokens,
  useNavigate,
  get_default_data
} : SankeyAppTypes) => {





  //- AFMSankey
  const [flux_colormap] = useState('no_colormap')

  //- login info
  const [name_user,set_name_user] =useState('')

  // const {units_names} = data
  // let units = undefined
  // if (units_names  && units_names.length > 0 && units_names[0].length > 0) {
  //   units = [units_names[0][0], 'Naturelles']
  // }
  // const has_uncert = Object.keys(data.fluxTags).filter(key => key === 'Uncert').length > 0
  // const flux_tags_visible = Object.keys(data.fluxTags).filter(tags_key => tags_key === 'flux_types' || tags_key === 'Uncert' ).length > 0
  // const [data_type] = useState('output')
  // const scale = d3.scaleLinear()
  //   .domain([0, +initial_sankey_data.user_scale])
  //   .range([0, 100])

  //- All

  const mode_selection = useRef('s')
  const selected_link = useRef(has_sankey_suite ? afm_default_link(initial_sankey_data) : OpenSankeyUtils.default_link(initial_sankey_data) )
  const selected_node = useRef(has_sankey_suite ? afm_default_node(initial_sankey_data) : OpenSankeyUtils.default_node(initial_sankey_data) )
  const multi_selected_nodes = useRef([])
  const multi_selected_links = useRef([])
  const multi_selected_label = useRef([])
  const button_ref = useRef<HTMLLabelElement>(null)
  const accordion_ref = useRef<HTMLDivElement>(null)
  const links_accordion_ref = useRef<HTMLDivElement>(null)
  const nodes_accordion_ref = useRef<HTMLDivElement>(null)
  const [data,set_data] = useState<SuiteSankeyData>(initial_sankey_data)
  const navbar_logo = !window.SankeyToolsStatic ? logo : window.sankey.logo as string
  const navbar_logo_width = !window.SankeyToolsStatic ? 100 : window.sankey.logo_width
  const navbar_app_version = !window.SankeyToolsStatic ? 'Version 1.0.0' : ''

    //- OpenSankey+
    const [view, set_postview] = useState(data.current_view)
    const [current_data,set_current_data]=useState<SuiteSankeyData>()
    const [animating,set_animating] = useState(false)
    const [show_toast_limit_node,set_show_toast_limit_node]=useState(false)

  //- Styles
  const [selected_style_link, set_selected_style_link] = useState('default')
  const [selected_style_node, set_selected_style_node] = useState('default')



  // For SankeyDraw
  const [alt_key_pressed,set_alt_key_pressed] = useState(false)
  const [first_selected_node,set_first_selected_node] = useState({})
  const [show_agregation, set_show_agregation] = useState(false)
  const [agregation_node, set_agregation_node] = useState('')
  const [is_agregation, set_is_agregation] = useState(true)

  const [user_scale, set_user_scale] = useState(data.user_scale)

  const set_view=(s:string)=>{
    data.current_view=s
    if(current_data){
      current_data.current_view=s
    }
    set_postview(s)
  }

  // Réinitialise data et vide les noeud/liens sélectionnés
  const reinitialization = () => {
    // const data = OpenSankeyUtils.default_sankey_data()
    const data = get_default_data()
    multi_selected_nodes.current = []
    multi_selected_links.current = []
    multi_selected_label.current = []
    localStorage.removeItem('diff')
    localStorage.removeItem('data')
    localStorage.removeItem('initial_data')
    set_selected_style_node('default')
    set_selected_style_link('default')
    set_current_data(undefined)
    set_data({ ...data })
    set_user_scale(data.user_scale)
    set_view(data.current_view)
    set_current_data(undefined)
    sessionStorage.setItem('dismiss_warning_sankey_plus','0')
    sessionStorage.setItem('dismiss_warning_sankey_mfa','0')
  }


  // For OpenSankeyMenuConfigurationLinks
  const newEntries = new Map(Object.entries(data.dataTags).map(([dataTagKey, dataTag]) => {
    return (Object.keys(dataTag.tags).length > 0) ? [
      dataTagKey,
      Object.entries(dataTag.tags).filter(tag => tag[1].selected).length > 0 ? Object.entries(dataTag.tags).filter(tag => tag[1].selected)[0][0] : Object.keys(dataTag.tags)[0]] : ['n', 'n']
  }))
  const dataTagsSelected = Object.fromEntries(newEntries)
  const [tags_selected, set_tags_selected] = useState(dataTagsSelected)
  if (Object.keys(tags_selected).length !== Object.keys(dataTagsSelected).length) {
    set_tags_selected(dataTagsSelected)
  }
  const [displayed_value,set_displayed_value]=useState('')

  //- Modals and Dialogs
  const [show_draw, set_show_draw] = useState(false)

  // Aggregation de sankeyPlus
  // const [parent_visible,set_parent_visible] = useState(false)
  // const [cube_dimension,set_cube_dimension] = useState(
  //   Object.values(data.nodeTags).filter(tag=>tag.banner == 'level').length > 0 ? Object.entries(data.nodeTags).filter(([,tag])=>tag.banner == 'level')[0][0] : 'Primaire'
  // )


  if(has_open_sankey_plus && !data.view){
    data.view=[]
    set_data({...data})
  }

  const suite_convert_data=(data:SuiteSankeyData)=>{
    let changed=false
    if(!data.view){
      data.view=[]
      changed=true
    }
    if(!data.current_view){
      data.current_view='none'
      changed=true
    }
    if( !data.labels){
      data.labels={}
      changed=true
    }

    if(changed){
      set_data({...data})
    }

  }
  // Convertie data pour prendre mettre a jour un data qui ne contiendrais pas certains paramètre verouiller derrière des modules
  suite_convert_data(data)
  if (data.view && data.view.length > 0 && !current_data) {
    set_current_data({...data})
    if(data.current_view!=='none'){
      const view_to_display=SankeyViews.get_data_from_view(data,data.current_view)
      set_view(data.current_view)
      set_data(view_to_display)
    }
  }

  const size_of_draw_zone=(Object.values(data.labels).length>0)?sankey_plus_min_width_and_height:OpenSankeyDrawFunction.min_width_and_height
  const SuiteGetLinkValue=(has_sankeydev)?devGetLinkValue:OpenSankeyUtils.getLinkValue

  //- End of build menus

  //- 3. Sankey Draws
  useBeforeunload((event : BeforeUnloadEvent) => {
    event.preventDefault()
    if(view==='none'){
      localStorage.setItem('data', LZString.compress(JSON.stringify(data)))
    } else {
      localStorage.setItem('data', LZString.compress(JSON.stringify(current_data)))
    }
  })

  const select_node=(n: SuiteSankeyNode) => {
    selected_node.current = n
  }
  const default_select_link=(l: SuiteSankeyLink) => {selected_link.current = l}

  const default_node_arrow_visible=(data:SuiteSankeyData,n: SuiteSankeyNode) => !n.node_visible || (n.inputLinksId.length === 0) || (!data.links[n.inputLinksId[0]].arrow) ? false : true

  const position = data.static_sankey ? 'relative' : 'absolute'


  useEffect(() => {
  if (animating) {
    return
  }
  const suiteDragging=SankeyPlusGradient.dragging
  // Call the function that add nodes to the sankey
  OpenSankeyDrawNodes(data,set_data,
    nodes_accordion_ref,links_accordion_ref,
    multi_selected_nodes,multi_selected_links,
    mode_selection,
    first_selected_node,set_first_selected_node,
    accordion_ref,button_ref,
    set_agregation_node,set_is_agregation,set_show_agregation,
    select_node,

    alt_key_pressed,
    data.static_sankey,
    position,
    (has_sankey_suite) ? nodeTooltipsContent : OpenSankeyTooltip.nodeTooltipsContent,
    (has_sankey_suite)?((flux_colormap === 'Uncert') ? uncert_link_text : link_text):OpenSankeyUtils.link_text, //link_text
    size_of_draw_zone,SuiteGetLinkValue,suiteDragging
    )
  OpenSankeyDrawNodesLabel(data,set_data,multi_selected_nodes,SuiteGetLinkValue)

  // Dessine les éléments pris en comptes par Sankey+ même si l'user n'a pas activé cette license, cependant il ne peut pas les modifier
  SankeyPlusDrawNodesIcon(data,mode_selection,data.static_sankey,nodeTooltipsContent)
  const tmpSankeyToolTip=d3.select('.sankey-tooltip')
  SankeyPlusNodeClickEvent(data,set_animating,tmpSankeyToolTip)
  SankeyPlusDrawLabels(data,set_data,multi_selected_label,accordion_ref,button_ref,alt_key_pressed,size_of_draw_zone)
  SankeyPlusDrawNodesFO(data,mode_selection,data.static_sankey,nodeTooltipsContent)
  d3.selectAll(' .opensankey #svg #sankey_def').remove()

  const suiteLinkStroke=SankeyPlusGradient.linkStroke
  const suiteDrawArrows=SankeyPlusGradient.SankeyPlusDrawArrows
  // const suiteDrawArrows= OpenSankeyDrawFunction.drawArrows
  // Call the function that add links to the sankey
  OpenSankeyDrawLinks(
    data,links_accordion_ref,
    multi_selected_links,
    mode_selection,
    accordion_ref,
    button_ref,
    default_select_link,
    alt_key_pressed,
    data.static_sankey,position,
    has_sankey_suite ? node_arrow_visible :default_node_arrow_visible,
    (has_sankey_suite) ? linkTooltipsContent : OpenSankeyTooltip.linkTooltipsContent,
    (has_sankey_suite)?((flux_colormap === 'Uncert') ? uncert_link_text : link_text):OpenSankeyUtils.link_text, //link_text
    SuiteGetLinkValue,set_data,set_displayed_value,tags_selected,set_tags_selected,suiteLinkStroke,suiteDrawArrows
  )
  OpenSankeyDrawLegend(data,SuiteGetLinkValue)
    if(has_sankeydev){
      hiddeLoneNodes(data,SuiteGetLinkValue)
      searchAndRestoreLoneNodes(data)
    }
  }, [show_draw, animating, data, first_selected_node, alt_key_pressed, position, has_sankey_suite, flux_colormap, size_of_draw_zone, SuiteGetLinkValue, tags_selected, has_sankeydev])

  // const shortcut_modale=OpenSankeyModalShortcut(t,showShortcut,setshowShortcut,additional_shortcut_item)
  //Event listener sur les touche du clavier
  //Réagis à :
  //-Flêches qui déplace les noeuds sélectionnés
  //-Echape qui ferme la navbar
  //-Ctrl+S qui sauvegarde une vue


  const d= (
    <div style={{ 'backgroundColor' : 'WhiteSmoke' }}>
      <>
      <SankeySuiteMenu
        data={data}
        set_data={set_data}
        get_default_data={get_default_data}
        displayed_value={displayed_value}
        set_displayed_value={set_displayed_value}
        set_animating={set_animating}
        multi_selected_nodes={multi_selected_nodes}
        multi_selected_links={multi_selected_links}
        multi_selected_label={multi_selected_label}
        selected_node={selected_node}
        selected_link={selected_link}
        button_ref={button_ref}
        accordion_ref={accordion_ref}
        links_accordion_ref={links_accordion_ref}
        nodes_accordion_ref={nodes_accordion_ref}
        exemple_menu={exemple_menu}
        formations_menu={formations_menu}
        logo={logo}
        logo_terriflux={logo_terriflux}
        token={token}
        has_sankey_suite={has_sankey_suite}
        has_open_sankey_plus={has_open_sankey_plus}
        has_sankeydev={has_sankeydev}
        unsetTokens={unsetTokens}
        useNavigate={useNavigate}
        mode_selection={mode_selection}
        user_scale={user_scale}
        set_user_scale={set_user_scale}
        SuiteGetLinkValue={SuiteGetLinkValue}
        tags_selected={tags_selected}
        set_tags_selected={set_tags_selected}
        current_data={current_data}
        set_current_data={set_current_data}
        reinitialization={reinitialization}
        selected_style_node={selected_style_node}
        set_selected_style_node={set_selected_style_node}
        selected_style_link={selected_style_link}
        set_selected_style_link={set_selected_style_link}
        view={view}
        set_view={set_view}
        first_selected_node={first_selected_node}
        set_first_selected_node={set_first_selected_node}
        show_toast_limit_node={show_toast_limit_node}
        set_show_toast_limit_node={set_show_toast_limit_node}
        size_of_draw_zone={size_of_draw_zone}
        name_user={name_user}
      />
      {//Ajout d'un delay pour laisser le temps au Menu de render pour ensuite utiliser sa hauteur afin d'ajouter un margin top au draw
      }
      {useEffect(() => {
        const timer = setTimeout(() => {
          set_show_draw(true)
          OpenSankeyUtils.adjust_sankey_zone(data,OpenSankeyDrawFunction.min_width_and_height)
        }, 100)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [])}

      <SankeyDraw
        data={data}
        set_data={set_data}
        animation={animating}
        multi_selected_nodes={multi_selected_nodes}
        multi_selected_label={multi_selected_label}
        multi_selected_links={multi_selected_links}

        button_ref={button_ref}

        linkTooltipsContent={has_sankey_suite ? linkTooltipsContent : OpenSankeyTooltip.linkTooltipsContent }
        mode_selection={mode_selection}

        first_selected_node={first_selected_node}
        set_first_selected_node={set_first_selected_node}
        show_agregation={show_agregation}
        set_show_agregation={set_show_agregation}
        agregation_node={agregation_node}
        is_agregation={is_agregation}

        set_alt_key_pressed={set_alt_key_pressed}
        min_width_and_height={size_of_draw_zone}
        getLinkValue={SuiteGetLinkValue}
        token={token}
        set_show_toast_limit_node={set_show_toast_limit_node}
      />

    </>
    </div>
  )

  if (window.SankeyToolsStatic) {
    return (<>{d}</>)
  }
  return (
    <Router>
      <Routes>
        <Route path='/license_register' element={
          <LicenseCheckIn
            navbar_logo={navbar_logo}
            navbar_logo_width={navbar_logo_width}
            navbar_app_version={navbar_app_version}
            setToken={set_token}
            useNavigate={useNavigate}
            data={data}
            set_data={set_data}
          />}
        />
        <Route path='/login' element={
          <Login
            navbar_logo={navbar_logo}
            navbar_logo_width={navbar_logo_width}
            navbar_app_version={navbar_app_version}
            setToken={set_token}
            setOpenSankeyPlus={set_open_sankey_plus}
            setSankeySuite={set_sankey_suite}
            setSankeyDev={set_sankeydev}
            useNavigate={useNavigate}
            data={data}
            set_data={set_data}
            set_name_user={set_name_user}
          />}
        />
        <Route path='/dashboard' element={
          <Dashboard
            navbar_logo={navbar_logo}
            navbar_logo_width={navbar_logo_width}
            navbar_app_version={navbar_app_version}
            token={token}
            exemple_menu={exemple_menu}
            multi_selected_label={multi_selected_label}
            multi_selected_links={multi_selected_links}
            multi_selected_nodes={multi_selected_nodes}
            data={data}
            set_data={set_data}
            unsetTokens={unsetTokens}
            useNavigate={useNavigate}
            reinitialization={reinitialization}
            set_user_scale={set_user_scale}
          />}
        />
        <Route path='/account' element={
          <Account
            navbar_logo={navbar_logo}
            navbar_logo_width={navbar_logo_width}
            navbar_app_version={navbar_app_version}
            token={token}
            setOpenSankeyPlus={set_open_sankey_plus}
            setSankeySuite={set_sankey_suite}
            setSankeyDev={set_sankeydev}
            unsetTokens={unsetTokens}
            useNavigate={useNavigate}
            data={data}
            set_data={set_data}
          />}
        />
        <Route path='/' element={d}/>
      </Routes>
    </Router>
  )
}


export default SankeyApp



